import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, Twitter, CreditCard, Star, Wifi } from 'lucide-react';
import { contactInfo } from '../mock/fareData';

const Footer = () => {
  return (
    <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white border-t border-cyan-500/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <img 
                src="/images/taxi98-logo.png" 
                alt="Taxi-98"
                className="h-12 w-auto"
              />
            </div>
            <p className="text-gray-400 text-sm">
              Your ride across Nassau, made easy. Professional taxi service with official government rates.
            </p>
            
            {/* Social Media Links */}
            <div className="flex items-center space-x-4 pt-2">
              <a
                href={contactInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5 text-cyan-400" />
              </a>
              <a
                href={contactInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5 text-cyan-400" />
              </a>
              <a
                href={contactInfo.social.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="Twitter/X"
              >
                <Twitter className="w-5 h-5 text-cyan-400" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-cyan-400 font-bold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 text-sm">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/book" className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 text-sm">
                  Book a Ride
                </Link>
              </li>
              <li>
                <Link to="/flights" className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 text-sm">
                  Flight Info
                </Link>
              </li>
              <li>
                <Link to="/fare-calculator" className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 text-sm">
                  Fare Calculator
                </Link>
              </li>
              <li>
                <Link to="/hey-taxi" data-testid="footer-hey-taxi-link" className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 text-sm">
                  Hey Taxi!
                </Link>
              </li>
              <li>
                <Link to="/reviews" className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 text-sm">
                  Customer Reviews
                </Link>
              </li>
              <li>
                <Link to="/#contact" className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 text-sm">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/admin" className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 text-sm">
                  Admin Dashboard
                </Link>
              </li>
              {/* 2026-06-04: Sun Med Portal relocated from main nav into Quick Links (still private, route unchanged) */}
              <li>
                <Link
                  to="/sun-med-booking"
                  data-testid="footer-sun-med-portal-link"
                  className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 text-sm inline-flex items-center gap-2"
                >
                  <span>Sun Med Portal</span>
                  <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-blue-500/10 text-blue-300 rounded border border-blue-400/30">
                    Private
                  </span>
                </Link>
              </li>
            </ul>
          </div>

          {/*
            Popular Transportation — 2026-09-06
            Internal-linking column that surfaces the four dedicated SEO
            landing pages (Nassau Airport Transfers, LPIA to Atlantis,
            LPIA to Baha Mar, Nassau Cruise Port Transportation).
          */}
          <div data-testid="footer-popular-transportation">
            <h3 className="text-cyan-400 font-bold text-lg mb-4">Popular Transportation</h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/nassau-airport-transfers"
                  data-testid="footer-nassau-airport-transfers-link"
                  className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 text-sm"
                >
                  Nassau Airport Transfers
                </Link>
              </li>
              <li>
                <Link
                  to="/lpia-to-atlantis"
                  data-testid="footer-lpia-to-atlantis-link"
                  className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 text-sm"
                >
                  LPIA to Atlantis Transportation
                </Link>
              </li>
              <li>
                <Link
                  to="/lpia-to-baha-mar"
                  data-testid="footer-lpia-to-baha-mar-link"
                  className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 text-sm"
                >
                  LPIA to Baha Mar Transportation
                </Link>
              </li>
              <li>
                <Link
                  to="/cruise-port-transportation"
                  data-testid="footer-cruise-port-transportation-link"
                  className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 text-sm"
                >
                  Nassau Cruise Port Transportation
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-cyan-400 font-bold text-lg mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                <div>
                  <a href={`tel:${contactInfo.phone}`} className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 text-sm">
                    {contactInfo.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                <div className="flex flex-col space-y-2">
                  <a href={`mailto:${contactInfo.email}`} className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 text-sm flex items-center space-x-2">
                    <Mail className="w-3.5 h-3.5 text-cyan-400" />
                    <span>{contactInfo.email}</span>
                  </a>
                  <a href={`mailto:${contactInfo.emailStephen}`} className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 text-sm flex items-center space-x-2">
                    <Mail className="w-3.5 h-3.5 text-cyan-400" />
                    <span>{contactInfo.emailStephen}</span>
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                <div className="text-gray-400 text-sm">
                  {contactInfo.address}<br />
                  {contactInfo.city}
                </div>
              </li>
            </ul>
          </div>

          {/* Payment & Hours */}
          <div>
            <h3 className="text-cyan-400 font-bold text-lg mb-4">We Accept</h3>
            
            {/* Payment Methods */}
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="bg-white px-3 py-2 rounded flex items-center justify-center">
                  <span className="text-blue-600 font-bold text-sm">VISA</span>
                </div>
                <div className="bg-white px-3 py-2 rounded flex items-center justify-center">
                  <span className="text-red-600 font-bold text-sm">MC</span>
                </div>
                <div className="bg-white px-3 py-2 rounded flex items-center justify-center">
                  <span className="text-blue-500 font-bold text-xs">AMEX</span>
                </div>
                <div className="bg-white px-3 py-2 rounded flex items-center justify-center">
                  <span className="text-orange-500 font-bold text-xs">DISCOVER</span>
                </div>
                <div className="bg-white px-3 py-2 rounded flex items-center justify-center">
                  <span className="text-gray-900 font-bold text-xs">Apple&nbsp;Pay</span>
                </div>
                <div className="bg-white px-3 py-2 rounded flex items-center justify-center">
                  <span className="text-blue-700 font-bold text-xs">PayPal</span>
                </div>
              </div>
              <p className="text-gray-400 text-sm flex items-center" data-testid="footer-payment-compact">
                <CreditCard className="w-4 h-4 mr-2 text-green-400" />
                Pre-Booking Available • Major Credit Cards • Apple Pay / Tap-to-Pay • PayPal • Cash Accepted
              </p>
              {/* 2026-06-04 — Free Wi-Fi quick info badge */}
              <p
                data-testid="footer-wifi-info"
                className="text-gray-400 text-sm flex items-center"
              >
                <Wifi className="w-4 h-4 mr-2 text-cyan-400" />
                Free Wi-Fi Available
              </p>
            </div>

            {/* Service Hours */}
            <div className="mt-6">
              <h3 className="text-cyan-400 font-bold text-lg mb-2">Service Hours</h3>
              <p className="text-gray-400 text-sm">
                <strong className="text-white">Mon-Sat:</strong> 5:00 AM - 11:00 PM<br />
                <strong className="text-white">Sunday:</strong> Pre-arranged only
              </p>
            </div>
          </div>
        </div>

        {/* Reviews & Social Proof — expanded 2026-06-26 (added Google Maps, Facebook, Visit Taxi-98) */}
        <div className="mt-8 py-6 border-t border-cyan-500/20">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center space-x-2 flex-shrink-0">
              <Star className="w-5 h-5 text-yellow-400" />
              <span className="text-gray-300 font-semibold">Reviews &amp; Social Proof:</span>
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3 md:justify-end" data-testid="footer-review-links">
              <a
                data-testid="footer-google-review-link"
                href="https://g.page/r/CRsSkN508n5DECA/review"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Leave a Google review (opens in new tab)"
                className="px-3 py-2 bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/30 text-blue-300 rounded-lg text-sm transition-colors flex items-center space-x-2"
              >
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <span>Google Review</span>
              </a>
              <a
                data-testid="footer-google-maps-link"
                href="https://goo.gl/maps/ZLgKpoDGJR3ahu1y6?g_st=ac"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View Taxi-98 on Google Maps (opens in new tab)"
                className="px-3 py-2 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300 rounded-lg text-sm transition-colors flex items-center space-x-2"
              >
                <MapPin className="w-4 h-4" />
                <span>Google Maps</span>
              </a>
              <a
                data-testid="footer-tripadvisor-link"
                href="https://www.tripadvisor.com/Attraction_Review-g147416-d34296506-Reviews-Taxi_98-Nassau_New_Providence_Island_Bahamas.html"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Review Taxi-98 on TripAdvisor (opens in new tab)"
                className="px-3 py-2 bg-green-500/15 hover:bg-green-500/25 border border-green-500/30 text-green-300 rounded-lg text-sm transition-colors flex items-center space-x-2"
              >
                <Star className="w-4 h-4" />
                <span>TripAdvisor</span>
              </a>
              <a
                data-testid="footer-facebook-review-link"
                href="https://www.facebook.com/share/1FwnCAwDLX/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow or review Taxi-98 on Facebook (opens in new tab)"
                className="px-3 py-2 bg-[#1877f2]/15 hover:bg-[#1877f2]/25 border border-[#1877f2]/40 text-[#7eb6ff] rounded-lg text-sm transition-colors flex items-center space-x-2"
              >
                <Facebook className="w-4 h-4" />
                <span>Facebook</span>
              </a>
              <Link
                data-testid="footer-taxi98-reviews-link"
                to="/reviews"
                aria-label="See Taxi-98 customer reviews on our website"
                className="px-3 py-2 bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/30 text-cyan-300 rounded-lg text-sm transition-colors flex items-center space-x-2"
              >
                <Star className="w-4 h-4" />
                <span>Taxi-98 Website</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-cyan-500/20">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © 2026 Taxi-98 Bahamas. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 text-sm">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 text-sm">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
